import { useMemo } from 'react';
import type { ApprovedPayRun } from '../types';
import { formatDate, formatPeriod } from '../format';

interface FilterBarProps {
  runs: ApprovedPayRun[];
  runId: string | null;
  disabled: boolean;
  onSelectRun: (id: string | null) => void;
}

/**
 * Pay-run selection. The pay run is the unit a check batch belongs to, so the controls pick one of the Approved Payroll Review runs: the year
 * narrows the pay days, and the pay period is read from the chosen run. "Pay Schedule Period" (for example twice a month) is not provided by the
 * Payroll Review run, so it is shown disabled rather than guessed (BACKEND GAP).
 */
export function FilterBar({ runs, runId, disabled, onSelectRun }: FilterBarProps) {
  const selected = runs.find((r) => r.id === runId) ?? null;
  const years = useMemo(() => [...new Set(runs.map((r) => r.payDate.slice(0, 4)))].sort().reverse(), [runs]);
  const year = selected ? selected.payDate.slice(0, 4) : (years[0] ?? '');
  const payDays = runs.filter((r) => r.payDate.startsWith(year));

  return (
    <div className="wc-filters">
      <label className="wc-field">
        <span>Business Pay Schedule</span>
        <select
          value={year}
          disabled={disabled || years.length === 0}
          onChange={(e) => {
            const first = runs.find((r) => r.payDate.startsWith(e.target.value));
            onSelectRun(first?.id ?? null);
          }}
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </label>
      <label className="wc-field">
        <span>Pay Schedule Period</span>
        <select disabled title="The pay schedule period is not provided by the payroll run yet.">
          <option>Not available yet</option>
        </select>
      </label>
      <label className="wc-field">
        <span>Pay Day</span>
        <select value={runId ?? ''} disabled={disabled || payDays.length === 0} onChange={(e) => onSelectRun(e.target.value || null)}>
          <option value="">Select a pay day</option>
          {payDays.map((r) => <option key={r.id} value={r.id}>{formatDate(r.payDate)}</option>)}
        </select>
      </label>
      <label className="wc-field">
        <span>Select Pay Period</span>
        <input type="text" readOnly value={selected ? formatPeriod(selected.periodStartDate, selected.periodEndDate) : ''} placeholder="Select a pay day" />
      </label>
      <button type="button" className="wc-btn wc-btn--dark" disabled={disabled || runId === null} onClick={() => onSelectRun(null)}>
        Clear Filter
      </button>
    </div>
  );
}
